import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { AccountGroup } from '@galaxy/account-group';
import { BadgeColor } from '@vendasta/galaxy/badge/src/badge.component';
import { TaskInterface } from '@vendasta/task';
import { forkJoin, Observable, ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { OrganizationInfoInterface, OrganizationType } from '../shared/interfaces';
import { SendMessageDialogService } from './send-message-dialog.service';

export interface ConversationBadge {
  participantType: string;
  badgeColor: BadgeColor;
}

export interface DialogData {
  partnerId: string;
  accountGroupInfo: AccountGroup;
  project: TaskInterface;
  prefilledMessage?: string;
}

@Component({
  selector: 'project-tracker-send-message-dialog',
  templateUrl: './send-message-dialog.component.html',
  styleUrls: ['./send-message-dialog.component.scss'],
})
export class SendMessageDialogComponent implements OnInit {
  private loading$$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  loading$: Observable<boolean> = this.loading$$.asObservable();
  organizations: OrganizationInfoInterface[];
  selectedOrg: OrganizationInfoInterface;
  defaultSelection = false;

  constructor(
    private sendMsgDialogService: SendMessageDialogService,
    public dialogRef: MatDialogRef<SendMessageDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
  ) {}

  ngOnInit(): void {
    this.loading$$.next(true);
    this.organizations = this.buildProjectOrganizations(
      this.data?.project,
      this.data?.partnerId,
      this.data?.accountGroupInfo,
    );
    if (this.organizations?.length < 2) {
      this.defaultSelection = true;
      this.selectedOrg = this.organizations[0];
    }
    forkJoin(this.organizations.map((org) => org.name))
      .pipe(take(1))
      .subscribe(() => {
        this.loading$$.next(false);
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  selectOrg(event: MatRadioChange): void {
    this.selectedOrg = this.organizations.find((organization) => organization.id === event.value);
  }

  sendMessage(): void {
    this.dialogRef.close(this.selectedOrg);
  }

  getConversationBadge(organization: any): ConversationBadge {
    switch (organization.type) {
      case OrganizationType.VENDOR:
        return organization.deleted
          ? { participantType: 'SEND_MESSAGE_DIALOG.LABELS.INACTIVE', badgeColor: 'grey' }
          : { participantType: 'SEND_MESSAGE_DIALOG.LABELS.VENDOR', badgeColor: 'purple' };
      case OrganizationType.ACCOUNT_GROUP:
        return organization.deleted
          ? { participantType: 'SEND_MESSAGE_DIALOG.LABELS.ACCOUNT_DELETED', badgeColor: 'grey' }
          : { participantType: 'SEND_MESSAGE_DIALOG.LABELS.ACCOUNT', badgeColor: 'blue' };
      case OrganizationType.PARTNER:
        return organization.deleted
          ? { participantType: 'SEND_MESSAGE_DIALOG.LABELS.INACTIVE', badgeColor: 'grey' }
          : { participantType: 'SEND_MESSAGE_DIALOG.LABELS.PARTNER', badgeColor: 'green' };
    }
  }

  private buildProjectOrganizations(
    project: TaskInterface,
    currentPartnerId: string,
    accountGroupInfo: AccountGroup,
  ): OrganizationInfoInterface[] {
    return this.sendMsgDialogService.buildProjectOrganizations(project, currentPartnerId, accountGroupInfo);
  }
}
